import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { FleetPage, Notice } from '../../sso-workspace/fleet-page'
import { contentGet } from '../../email-activity/content-api'
import { getFacets } from '../audit-api'
import { AuditFilters } from '../audit-filters'
import { AuditTimeline } from '../audit-timeline'
import { actorChip } from '../entry-format'
import { applyPivot, contentPart, filtersToParams, filtersToSearch, insightParts, isAllContent, NO_INSIGHT_NARROWING, withoutNetwork } from '../filters-model'
import { DayChip, useDayRange, useResultsInView } from '../day-range'
import { entryPath } from '../record-links'
import { ENTRY_STATE } from '../entry-route'
import { useFiltersInURL } from '../use-filters-url'
import { HealthBanner } from '../health-banner'
import { CalendarStrip } from '../insights/calendar-strip'
import { OrgPicker } from '../../org-search/org-picker'
import { AuditTable } from './audit-table'
import { ExportDialog } from './export-dialog'
import { RecentExports } from './recent-exports'
import { SessionElsewhere, useSessionLabel } from '../session-label'
import { ActiveFilters, withinPart } from '../active-filters'
import '../audit.scss'
import './audit-log.scss'
import { SEARCH_FIELD_PROPS } from '../../component/search-field-props'

// Insights (Task 11) is its own chunk, ECharts and all: only a reader who
// opens it pays for it.
const InsightsView = React.lazy(() => import('../insights/insights-view'))

const SEARCH_DEBOUNCE_MS = 300
// The Table grid's own search box (TableAgInfinite queryKey "tb").
const TABLE_SEARCH = 'tb.search'
const ORG_ID = /^[1-9][0-9]{0,9}$/
// A pivot from Insights shows the timeline: it drops ?view and the
// Insights grid's own URL keys in the same history entry.
const LEAVE_INSIGHTS = k => k === 'view' || k.startsWith('launches.')
// How long "Export started" stays (an object so tests can shorten it).
export const NOTICE = { ms: 8000 }

const PURPOSE = {
  org: 'Everything that happened in your organization: who changed what, when, from where and with which app.',
  sys: 'Everything that happened, in one organization or all of them.'
}

// A learning-activity reader's filters: no "Network and location" (no IP or
// location, owner ruling 2026-10-04).
const NO_NETWORK = new Set(['category', 'who', 'type', 'when', 'app', 'toggles'])

// The organization filter's first choice (no ?org).
const ALL_ORGS = { value: 'all', label: 'All organizations' }

const listOf = v => (Array.isArray(v) ? v : Array.isArray(v && v.data) ? v.data : [])
const shortID = v => String(v || '').slice(0, 8)

// What a pivot narrowed the list to, as a removable chip (owner
// 2026-10-09; active-filters.js), or null: the narrowings the filter
// column has no control for. A session reads as its label ("This
// session", "Another session · Mac app · started …"), never its id; staff
// see the id on hover.
const pivotOf = (f, facets, session) => {
  if (f.request) return { key: 'request', text: 'Request', code: shortID(f.request), title: f.request, remove: { request: '' } }
  if (f.session) return { key: 'session', text: session.label, title: session.staff ? f.session : undefined, remove: { session: '' } }
  if (f.ip) return { key: 'ip', text: 'IP', code: f.ip, remove: { ip: '' } }
  if (f.actors.length === 1 && !f.groups.length) {
    const facet = ((facets && facets.actors) || []).find(a => a.key === f.actors[0])
    return { key: 'actor', text: `Only ${facet ? actorChip(facet).text : 'one person'}`, remove: { actors: [] } }
  }
  return null
}

const INSIGHT_REMOVE = { verb: { verbs: [] }, outcome: { outcomes: [] }, records: { recordFilters: [] } }
const capital = t => t[0].toUpperCase() + t.slice(1)

// The Audit log (spec §5; mockup "Audit log"): the organization's (or, for
// super admins, any or every organization's) entries as the timeline,
// filters on the left, search and Export… above. Super admins whose
// /capabilities says `table` also get the Table view; org admins never do.
//
// Every filter lives in the URL (useFiltersInURL); the page's own keys are
// view=table|insights and, for super admins, org=<id> (absent: all
// organizations). Insights (Task 11) reads the timeline's own params; the
// calendar strip sits above the timeline.
// params is computed once per filter change, so the window ("last 30 days")
// is fixed until the filters change and the feed isn't re-read on every
// render.
export const AuditLogPage = ({ sys = false, caps }) => {
  const history = useHistory()
  const location = useLocation()
  const [filters, setURLFilters] = useFiltersInURL()
  // the page's range (every view's), a calendar day as the range, and the
  // chip that goes back from it
  const { range, back, pickDay, clearDay, setFilters } = useDayRange(filters, setURLFilters)
  const search = useMemo(() => new URLSearchParams(location.search), [location.search])

  const canTable = Boolean(caps.table)
  const urlView = search.get('view')
  const view = canTable && urlView === 'table' ? 'table' : urlView === 'insights' ? 'insights' : 'timeline'
  const orgParam = search.get('org')
  const org = sys && ORG_ID.test(orgParam || '') ? orgParam : 'all'

  const filtersKey = filtersToSearch(filters)
  const scopeKey = sys ? org : ''
  const params = useMemo(
    () => ({ ...(sys ? (org === 'all' ? { scope: 'all' } : { orgID: org }) : {}), ...filtersToParams(withoutNetwork(filters, caps)) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scopeKey, filtersKey]
  )
  // The table's first block asks for a fresh window ("reload" reads to now).
  const tableWindow = useCallback(() => {
    const p = filtersToParams(withoutNetwork(filters, caps))
    return { ...(p.from ? { from: p.from } : {}), ...(p.to ? { to: p.to } : {}) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey])

  // What the reader is looking at: in the Table view the grid's own search
  // box is the text search, so it counts in the facets and the export too
  // (a facet count is what clicking it lists).
  const tableSearch = view === 'table' ? search.get(TABLE_SEARCH) || '' : ''
  const effectiveParams = useMemo(() => (tableSearch ? { ...params, q: tableSearch } : params), [params, tableSearch])

  // The organization's groups (Who), read once into state. Super admins
  // pick an organization with the picker, which searches content itself.
  const [groups, setGroups] = useState([])
  useEffect(() => {
    if (sys) return undefined
    let live = true
    contentGet('group').then(v => live && setGroups(listOf(v))).catch(() => {})
    return () => {
      live = false
    }
  }, [sys])

  // Facets for the current params; a newer read always wins, and a failed
  // one leaves the filters without counts.
  const [facets, setFacets] = useState(null)
  const [facetsError, setFacetsError] = useState(null)
  const generation = useRef(0)
  useEffect(() => {
    const mine = ++generation.current
    getFacets(effectiveParams).then(
      f => {
        if (mine !== generation.current) return
        setFacets(f || null)
        setFacetsError(null)
      },
      err => {
        if (mine !== generation.current) return
        setFacets(null)
        setFacetsError(err)
      }
    )
  }, [effectiveParams])
  useEffect(() => () => { generation.current++ }, [])

  // The search box: typing waits 300 ms before it becomes the q filter.
  const latest = useRef({ filters, setFilters })
  latest.current = { filters, setFilters }
  const [text, setText] = useState(filters.q)
  useEffect(() => setText(filters.q), [filters.q])
  useEffect(() => {
    if (text === latest.current.filters.q) return undefined
    const t = setTimeout(() => latest.current.setFilters({ ...latest.current.filters, q: text }), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [text])

  const replaceSearch = mutate => {
    const s = new URLSearchParams(location.search)
    mutate(s)
    const out = s.toString()
    history.replace({ pathname: location.pathname, search: out ? `?${out}` : '', state: location.state })
  }

  // The Table has its own search box (the grid's), so switching carries the
  // text across instead of keeping a search nobody can see.
  const setView = next => {
    if (next === view) return
    replaceSearch(s => {
      if (next === 'table') {
        s.set('view', 'table')
        if (s.get('q')) s.set(TABLE_SEARCH, s.get('q'))
        s.delete('q')
        return
      }
      if (next === 'insights') s.set('view', 'insights')
      else s.delete('view')
      if (s.get(TABLE_SEARCH)) s.set('q', s.get(TABLE_SEARCH))
      s.delete(TABLE_SEARCH)
    })
  }

  const setOrg = next =>
    replaceSearch(s => {
      if (next === 'all') s.delete('org')
      else s.set('org', next)
    })

  // Courses and folders by "nodes:<id>", for the within filter's chip: the
  // one the reader clicked, the one content's first page names, and every
  // place the loaded entries name.
  const [places, setPlaces] = useState({})
  const onItems = useCallback((items, record, within) => {
    setPlaces(prev => {
      let next = prev
      const add = p => {
        if (p && p.id && p.name && !next[`nodes:${p.id}`]) next = { ...next, [`nodes:${p.id}`]: p }
      }
      add(within)
      items.forEach(item => item.context && [item.context.course, item.context.folder].forEach(add))
      return next
    })
  }, [])
  const onPivot = ({ withinName, ...patch }) => {
    if (patch.within && withinName) setPlaces(prev => ({ ...prev, [patch.within]: { ...(prev[patch.within] || {}), name: withinName } }))
    setFilters({ ...filters, ...patch }, { push: true })
  }
  // Insights and the calendar strip narrow the timeline and show it.
  const pivotFromInsights = patch => setFilters(applyPivot(filters, patch), { push: true, drop: LEAVE_INSIGHTS })
  // The session the list is narrowed to, in words, and its entries in other
  // organizations (a count; staff also get a button to them).
  const staff = sys || Boolean(caps.sysAuditLog)
  const session = useSessionLabel(filters.session, params, { others: true })
  const pivot = pivotOf(filters, facets, { label: session.label, staff })
  const within = withinPart(filters.within, places[filters.within])
  const narrowed = [
    ...(pivot ? [pivot] : []),
    ...(within ? [within] : []),
    ...contentPart(filters).map(p => ({ ...p, text: capital(p.text), remove: { recordTypes: [] } })),
    ...insightParts(filters).map(p => ({ ...p, text: capital(p.text), remove: INSIGHT_REMOVE[p.key] }))
  ]
  const removeOne = part => setFilters({ ...filters, ...part.remove }, { push: true })
  const showEverything = () =>
    setFilters({ ...filters, request: '', session: '', ip: '', within: '', actors: [], ...NO_INSIGHT_NARROWING, ...(isAllContent(filters) ? { recordTypes: [] } : {}) }, { push: true })

  // a filter, range or view change keeps the results in view: never the
  // exports table below them (useResultsInView)
  const results = useRef(null)
  useResultsInView(results, `${scopeKey}|${filtersKey}|${view}`)

  const openEntry = entry => entry && history.push({ pathname: entryPath(entry.id, sys), search: location.search, state: ENTRY_STATE })

  const [exporting, setExporting] = useState(false)
  const [exportsKey, setExportsKey] = useState(0)
  const [started, setStarted] = useState(false)
  const openExport = useCallback(() => {
    setStarted(false)
    setExporting(true)
  }, [])
  const closeExport = useCallback(() => setExporting(false), [])
  const onStarted = useCallback(() => {
    setExporting(false)
    setStarted(true)
    setExportsKey(k => k + 1)
  }, [])
  // "Export started" goes away by itself (and when Export… opens again).
  useEffect(() => {
    if (!started) return undefined
    const t = setTimeout(() => setStarted(false), NOTICE.ms)
    return () => clearTimeout(t)
  }, [started])

  const orgPicker = sys &&
    <section className="au-facet au-org">
      <h3 className="au-facet-h">Organization</h3>
      <OrgPicker id="au-org" label="Organization" value={org} onChange={setOrg} allOption={ALL_ORGS} small />
    </section>

  return (
    <FleetPage title="Audit log" purpose={sys ? PURPOSE.sys : PURPOSE.org}>
      <HealthBanner />
      {started && <Notice tone="ok" role="status">Export started. It appears under Recent exports when it’s ready.</Notice>}

      <div className="au-two">
        <AuditFilters filters={filters} onChange={setFilters} facets={facets} error={facetsError} groups={groups} extra={orgPicker} show={caps.activityLog ? NO_NETWORK : undefined} sessionLabel={session.label} />

        <div className="au-feedcard" ref={results}>
          <DayChip range={range} back={back} onClear={clearDay} />
          <div className="au-feedbar">
            {view !== 'table'
              ? <input
                  {...SEARCH_FIELD_PROPS}
                  name="audit-search"
                  className="form-control form-control-sm au-search"
                  placeholder="Search people, records, values or a request ID"
                  aria-label="Search the audit log"
                  value={text}
                  maxLength={200}
                  onChange={e => setText(e.target.value)}
                />
              : <span className="au-spacer" />}
            <div className="au-seg" role="group" aria-label="Layout">
              <button type="button" className="btn sw-btn btn-sm" aria-pressed={view === 'timeline'} onClick={() => setView('timeline')}>Timeline</button>
              {canTable && <button type="button" className="btn sw-btn btn-sm" aria-pressed={view === 'table'} onClick={() => setView('table')}>Table</button>}
              <button type="button" className="btn sw-btn btn-sm" aria-pressed={view === 'insights'} onClick={() => setView('insights')}>Insights</button>
            </div>
            {caps.export && <button type="button" className="btn sw-btn btn-sm" onClick={openExport}>Export…</button>}
          </div>

          <ActiveFilters parts={narrowed} onRemove={removeOne} onClear={showEverything} />
          {filters.session && <SessionElsewhere sessionID={filters.session} summary={session.summary} error={session.error} staff={staff} sysPage={sys} filters={filters} />}

          {view === 'table' && <AuditTable params={params} window={tableWindow} sys={sys} onOpen={openEntry} />}
          {view === 'insights' &&
            <Suspense fallback={<p className="sw-hint">Loading charts…</p>}>
              <InsightsView params={params} onPivot={pivotFromInsights} onPickDay={pickDay} range={range} context="audit" sys={sys} />
            </Suspense>}
          {view === 'timeline' &&
            <>
              <CalendarStrip params={params} range={range} onPickDay={pickDay} />
              <AuditTimeline params={params} context="audit" sys={sys} caps={caps} onPivot={onPivot} onItems={onItems} emptyText="Nothing matches these filters in this time range." />
            </>}
        </div>
      </div>

      {caps.export && <RecentExports refreshKey={exportsKey} onExportAgain={openExport} />}

      {!caps.activityLog && <p className="small sw-hint au-credit">IP location by <a href="https://db-ip.com" target="_blank" rel="noreferrer">DB-IP</a></p>}

      <ExportDialog open={exporting} params={effectiveParams} range={filters} onClose={closeExport} onStarted={onStarted} />
    </FleetPage>
  )
}
